<script setup lang="ts">
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { ref, onMounted } from 'vue'
  import { QueueSettingsService } from '@/types/generated'
  import SelectField from '@/components/admin/form/SelectField.vue'

  const isLoading = ref(true)
  const isSaving = ref(false)
  const saveSuccess = ref(false)

  const maxWaitSeconds = ref(60)
  const maxRetryAttempts = ref(4)
  const retryIntervalSeconds = ref(15)
  const holdMusicTrack = ref('classical_chopin')

  const holdMusicOptions = [
    { value: 'ambient_exurb', title: 'Ambient — Exurb' },
    { value: 'ambient_taciturn', title: 'Ambient — Living Taciturn' },
    { value: 'ambient_plains', title: 'Ambient — Plains' },
    { value: 'classical_borghestral', title: 'Classical — Borghestral' },
    { value: 'classical_brahms', title: 'Classical — Brahms' },
    { value: 'classical_strings', title: 'Classical — Busy Strings' },
    { value: 'classical_chopin', title: 'Classical — Chopin' },
    { value: 'classical_waltz', title: 'Classical — Clockwork Waltz' },
    { value: 'classical_goodbye', title: 'Classical — Endless Goodbye' },
    { value: 'classical_flute', title: 'Classical — Flight of Young Hearts (Flute)' },
    { value: 'electronica_downtempo', title: 'Electronica — Downtempo' },
    { value: 'electronica_seawall', title: 'Electronica — Seawall Stepper' },
    { value: 'guitars_thought', title: 'Guitars — A Thought' },
    { value: 'softrock_melancholy', title: 'Soft Rock — Melancholy for a Sun-lit Day' },
    { value: 'softrock_sunny', title: 'Soft Rock — The First Sunny Sky' }
  ]

  // Use path-style S3 URLs (https://s3.amazonaws.com/bucket/key) instead of
  // virtual-hosted-style (https://bucket.s3.amazonaws.com/key) because the
  // Twilio bucket names contain dots, which breaks TLS certificate validation.
  const S3 = 'https://s3.amazonaws.com'

  const holdMusicUrls: Record<string, string> = {
    classical_chopin: `${S3}/com.twilio.music.classical/ith_chopin-15-2.mp3`,
    classical_waltz: `${S3}/com.twilio.music.classical/ClockworkWaltz.mp3`,
    classical_strings: `${S3}/com.twilio.music.classical/BusyStrings.mp3`,
    classical_brahms: `${S3}/com.twilio.music.classical/ith_brahms-116-4.mp3`,
    classical_borghestral: `${S3}/com.twilio.music.classical/MARKOVICHAMP-Borghestral.mp3`,
    classical_flute: `${S3}/com.twilio.music.classical/Mellotroniac_-_Flight_Of_Young_Hearts_Flute.mp3`,
    classical_goodbye: `${S3}/com.twilio.music.classical/oldDog_-_endless_goodbye_(instr.).mp3`,
    ambient_taciturn: `${S3}/com.twilio.music.ambient/aerosolspray_-_Living_Taciturn.mp3`,
    ambient_exurb: `${S3}/com.twilio.music.ambient/gurdonark_-_Exurb.mp3`,
    ambient_plains: `${S3}/com.twilio.music.ambient/gurdonark_-_Plains.mp3`,
    electronica_seawall: `${S3}/com.twilio.music.electronica/Kaer_Trouz_-_Seawall_Stepper.mp3`,
    electronica_downtempo: `${S3}/com.twilio.music.electronica/teru_-_110_Downtempo_Electronic_4.mp3`,
    softrock_melancholy: `${S3}/com.twilio.music.soft-rock/Fireproof_Babies_-_Melancholy_4_a_Sun-lit_day.mp3`,
    softrock_sunny: `${S3}/com.twilio.music.soft-rock/jacksontorreal_-_The_First_Sunny_Sky.mp3`,
    guitars_thought: `${S3}/com.twilio.music.guitars/Pitx_-_A_Thought.mp3`
  }

  const audioRef = ref<HTMLAudioElement | null>(null)
  const isPlaying = ref(false)

  const togglePlay = async () => {
    if (!audioRef.value) return
    if (isPlaying.value) {
      audioRef.value.pause()
      isPlaying.value = false
    } else {
      const url = holdMusicUrls[holdMusicTrack.value]
      if (!url) return
      audioRef.value.src = url
      try {
        await audioRef.value.play()
        isPlaying.value = true
      } catch {
        // Firefox may block cross-origin audio — open in new tab as fallback
        window.open(url, '_blank')
      }
    }
  }

  // Stop playback when track changes
  const stopPlayback = () => {
    if (audioRef.value) {
      audioRef.value.pause()
      audioRef.value.currentTime = 0
      isPlaying.value = false
    }
  }

  const settingsUuid = ref('')

  onMounted(async () => {
    try {
      const response = await QueueSettingsService.queueSettingsList()
      const data = response as any
      settingsUuid.value = data.uuid
      maxWaitSeconds.value = data.max_wait_seconds
      maxRetryAttempts.value = data.max_retry_attempts
      retryIntervalSeconds.value = data.retry_interval_seconds
      holdMusicTrack.value = data.hold_music_track
    } catch (e) {
      console.error('Failed to load queue settings', e)
    } finally {
      isLoading.value = false
    }
  })

  const save = async () => {
    isSaving.value = true
    saveSuccess.value = false
    try {
      await QueueSettingsService.queueSettingsPartialUpdate({
        uuid: settingsUuid.value,
        requestBody: {
          max_wait_seconds: maxWaitSeconds.value,
          max_retry_attempts: maxRetryAttempts.value,
          retry_interval_seconds: retryIntervalSeconds.value,
          hold_music_track: holdMusicTrack.value
        }
      })
      saveSuccess.value = true
      setTimeout(() => {
        saveSuccess.value = false
      }, 3000)
    } catch (e) {
      console.error('Failed to save queue settings', e)
    } finally {
      isSaving.value = false
    }
  }
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Queue Settings', to: ROUTES.ADMIN.VOICE.QUEUE_SETTINGS }
          ]"
        />
      </div>

      <!-- Settings Card -->
      <div
        class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
      >
        <h2 class="mb-1 text-lg font-semibold text-gray-800">Queue Settings</h2>
        <p class="mb-6 text-sm text-gray-500">
          Global defaults for the forwarding waiting feature. Per-organisation overrides can be set
          on each customer's JUPUS Phone AI panel.
        </p>

        <div
          v-if="isLoading"
          class="flex items-center justify-center py-12"
        >
          <div
            class="h-8 w-8 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600"
          ></div>
        </div>

        <div
          v-else
          class="max-w-lg space-y-5"
        >
          <!-- Max Wait Time -->
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
              Max. Wartezeit (Sekunden)
            </label>
            <input
              v-model.number="maxWaitSeconds"
              type="number"
              min="15"
              max="120"
              class="block w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:border-purple-500
                focus:ring-purple-500"
            />
            <p class="mt-1 text-xs text-gray-400">Bereich: 15–120 Sekunden</p>
          </div>

          <!-- Max Retry Attempts -->
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
              Max. Wahlwiederholungen
            </label>
            <input
              v-model.number="maxRetryAttempts"
              type="number"
              min="1"
              max="8"
              class="block w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:border-purple-500
                focus:ring-purple-500"
            />
            <p class="mt-1 text-xs text-gray-400">Bereich: 1–8 Versuche</p>
          </div>

          <!-- Retry Interval -->
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
              Intervall zwischen Versuchen (Sekunden)
            </label>
            <input
              v-model.number="retryIntervalSeconds"
              type="number"
              min="10"
              max="30"
              class="block w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:border-purple-500
                focus:ring-purple-500"
            />
            <p class="mt-1 text-xs text-gray-400">Bereich: 10–30 Sekunden</p>
          </div>

          <!-- Hold Music -->
          <div>
            <SelectField
              v-model="holdMusicTrack"
              name="holdMusicTrack"
              label="Wartemusik"
              :options="holdMusicOptions"
              @update:model-value="stopPlayback"
            />
            <div class="mt-2 flex items-center gap-3">
              <button
                type="button"
                class="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm
                  font-medium text-gray-700 transition-colors hover:bg-gray-50"
                @click="togglePlay"
              >
                <svg
                  v-if="!isPlaying"
                  class="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
                    clip-rule="evenodd"
                  />
                </svg>
                <svg
                  v-else
                  class="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M6.75 5.25a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H7.5a.75.75 0 0 1-.75-.75V5.25Zm7.5 0A.75.75 0 0 1 15 4.5h1.5a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H15a.75.75 0 0 1-.75-.75V5.25Z"
                    clip-rule="evenodd"
                  />
                </svg>
                {{ isPlaying ? 'Stopp' : 'Vorschau' }}
              </button>
              <span
                v-if="isPlaying"
                class="text-xs text-gray-400"
              >
                {{ holdMusicOptions.find((o) => o.value === holdMusicTrack)?.title }}
              </span>
            </div>
            <audio
              ref="audioRef"
              @ended="isPlaying = false"
            />
          </div>

          <!-- Save Button -->
          <div class="flex items-center gap-3 pt-2">
            <button
              :disabled="isSaving"
              class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-4 py-2.5
                text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
                focus:ring-purple-500 disabled:opacity-50"
              @click="save"
            >
              {{ isSaving ? 'Speichern...' : 'Speichern' }}
            </button>
            <span
              v-if="saveSuccess"
              class="text-sm font-medium text-green-600"
            >
              ✓ Gespeichert
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
